import {NativeBackService} from "../../_services/native-back.service";
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject, Input, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {Post, PostComment, PostCommentTree} from '../../_types/content';
import {buildCommentsTree} from '../../_utils/build-comments-tree';
import {SharedStateService} from '../../_services/shared-state.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, take} from 'rxjs';
import {Poll} from '../../_types/polls';
import {ConfirmationDialogData, CustomButtonToggleOption, ReplyData, ReplyEvent} from '../../_types/shared';
import {LocalStorageService} from '../../_services/local-storage.service';
import {ContentService} from '../../_services/content.service';
import {Community, UserAccessLevel} from '../../_types/communities';
import {AuthService} from '../../_services/auth.service';
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";
import {CommunitiesService} from "../../_services/communities.service";

@Component({
    selector: 'app-comments',
    templateUrl: './comments.component.html',
    styleUrl: './comments.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CommentsComponent implements OnDestroy {
    @Input({required: true}) post!: Post;
    commentsTree: PostCommentTree[] = [];
    pinnedCommentsTree: PostCommentTree[] = [];
    poll?: Poll;
    prefillComment?: string
    sortOptions: CustomButtonToggleOption[] = [
        {label: 'Newest first', value: 'new', icon: 'arrow_upward'},
        {label: 'Oldest first', value: 'old', icon: 'arrow_downward'}
    ];
    selectedSortOption!: CustomButtonToggleOption;
    userInfoMenuOpen: boolean = false;
    community: Community | null = null

    private readonly nativeBack = inject(NativeBackService);
    private unregisterNativeBack: () => void = () => {};

    constructor(
        @Inject(MAT_DIALOG_DATA) private data: {
            comments: PostComment[],
            post: Post,
            poll?: Poll,
            prefillComment?: string
        },
        public dialog: MatDialog,
        private readonly matDialog: MatDialogRef<CommentsComponent>,
        private readonly sharedStateService: SharedStateService,
        private readonly localStorageService: LocalStorageService,
        private readonly contentService: ContentService,
        private readonly communitiesService: CommunitiesService,
        private readonly cdr: ChangeDetectorRef,
        private readonly authService: AuthService,
    ) {
        this.unregisterNativeBack = this.nativeBack.register(this.matDialog.id, () => this.exitCheck());
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });
        this.post = data.post;
        if (data.comments) {
            this.commentsTree = buildCommentsTree(data.comments, this.selectedCommunity?.collapsed_by_default);
        }

        this.prefillComment = data.prefillComment;
        this.poll = data.poll
        if (!this.poll && data.post?.polls && data.post?.polls?.length > 0) {
            this.poll = data.post?.polls[0];
        }
        this.sharedStateService.getNewModalComments$()
            .pipe(
                filter((data): data is PostComment[] => !!data),
                takeUntilDestroyed()
            )
            .subscribe(comments => {
                this.commentsTree = buildCommentsTree(comments, this.selectedCommunity?.collapsed_by_default);
                this.cdr.detectChanges();
            })
        this.selectedSortOption = this.localStorageService.getItem<CustomButtonToggleOption>('commentsSortOption', this.sortOptions[0]);
        this.contentService.reportAction('post-open', 'post', this.post?.id, this.selectedCommunity?.id).subscribe(r => {
        });
    }

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    get selectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    ngOnDestroy(): void {
        this.unregisterNativeBack();
        this.sharedStateService.updateModalComments(null);
    }

    sortComments(option: CustomButtonToggleOption): void {
        this.selectedSortOption = option;
        this.localStorageService.setItem(option, 'commentsSortOption');
        this.contentService.getPostComments(this.post.id).pipe(take(1))
            .subscribe(res => this.sharedStateService.updateModalComments(res));

    }

    updateComment(event: ReplyEvent<ReplyData>): void {
        this.sharedStateService.getNewModalComments$();
    }

    exitCheck(): void {
        // check if any reply fields are opened
        let commentInProgress: boolean = false
        let textBoxes: HTMLCollectionOf<Element> = document.getElementsByClassName('replyField')
        const textBoxesArray: Element[] = Array.from(textBoxes);

        for (let box of textBoxesArray) {
            if (!box.classList.contains('closedState')) {
                // comment Opened
                commentInProgress = true;
            }
        }

        if (commentInProgress) {
            const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
                panelClass: 'appFixWindow',
                data: {
                    showSubmitBtn: true,
                    showCancelBtn: true,
                    message: 'Are you sure you want to exit? Changes that you have made will not be saved.',
                    title: 'Exit',
                    showHeader: true,
                }
            });
            dialogRef.afterClosed().subscribe(r => {
                if (r) {
                    this.matDialog.close()
                }
            });
        } else {
            this.matDialog.close()
        }
    }

}
